// @refresh reset — dev-only
import { Fragment, useState } from "react";
import { SectionBox } from "./SectionBox";

// ─────────────────────────────────────────────────────────────────────────────
// PLANNING — 3 zoom levels (2-Week · Month · 3-Month), one view each, populated
// with REAL state from every project + last week's meetings + our boards.
// ✓ = done this week · solid = planned ahead (tomorrow / next week / month / qtr).
// Data authored 2026-07-24 (Fri). Rolls from this week.
// ─────────────────────────────────────────────────────────────────────────────

const T = {
  ink: "#1B211E", ink2: "#4A534D", faint: "#8C948E",
  line: "#ECE7DF", hair: "#E3DED4", card: "#FFFFFF", paper: "#FAF7F2", bone: "#F2EDE4",
  now: "#EEF5F1", nowStroke: "#3F8F6F",
};
const FONT = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";
const EY: React.CSSProperties = { fontFamily: MONO, fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: T.faint };

type Act = { t: string; wk: number; day?: number; done?: boolean };
type Proj = { name: string; color: string; status: string; acts: Act[] };
// day: 0=Sun … 5=Fri(today) … 6=Sat(tomorrow). wk 0=this, 1=next, 2-3=this month, 5+=quarter.
const P: Proj[] = [
  { name: "Falcon", color: "#3F8F6F", status: "Closed ₪16K · Dan", acts: [
    { t: "Discovery meeting", wk: 0, day: 4, done: true }, { t: "Board + PRD built", wk: 0, day: 4, done: true }, { t: "Legal cleared", wk: 0, day: 4, done: true },
    { t: "Spin up Falcon agent", wk: 0, day: 6 }, { t: "First weekly 2h session", wk: 1, day: 1 }, { t: "Phase 1: voice property DB + CRM", wk: 1 },
    { t: "Brand + identity", wk: 3 }, { t: "Lead-capture website", wk: 6 } ] },
  { name: "MAX Security", color: "#2F6BB0", status: "Access next wk · Eyal", acts: [
    { t: "Agreed on the problems", wk: 0, day: 4, done: true }, { t: "Confirm signed Phase-1 terms", wk: 0, day: 6 },
    { t: "Meet Sakhri — connect system", wk: 1, day: 1 }, { t: "Meet the CEO", wk: 1, day: 2 }, { t: "Get data access + tinker", wk: 1 },
    { t: "Lock top-3 pilot automations", wk: 2 }, { t: "Isolation architecture research", wk: 2 }, { t: "Simulated-MAX POC", wk: 4 } ] },
  { name: "Consultancy", color: "#7A5B78", status: "Site + lead engine · Leo/Ben", acts: [
    { t: "Footer shipped to tailormind.co", wk: 0, day: 2, done: true }, { t: "Discovery hero design dialed in", wk: 0, day: 4, done: true },
    { t: "Ben's 5 lead-engine decisions", wk: 0, day: 6 }, { t: "Nail the first service (Discovery)", wk: 1 }, { t: "Real copy into the pages", wk: 1 },
    { t: "Build the other 3 services", wk: 2 }, { t: "Lead-engine provider bake-off", wk: 2 }, { t: "Promote site to prod", wk: 3 } ] },
  { name: "Boiling Offers", color: "#2C7E9E", status: "Live · Matan", acts: [
    { t: "21-workflow review call w/ Matan", wk: 0, day: 2, done: true }, { t: "In-app review feature live", wk: 0, day: 2, done: true },
    { t: "Matan signs off 21 workflows", wk: 1, day: 1 }, { t: "Implement approved revisions", wk: 2 } ] },
  { name: "Flame King", color: "#C0492F", status: "Diagnosed · Yogev", acts: [
    { t: "Diagnosed: NetSuite focus gap", wk: 0, day: 4, done: true }, { t: "FK dashboard incident recovered", wk: 0, day: 5, done: true },
    { t: "Draft NetSuite-focus plan", wk: 0, day: 6 }, { t: "Present plan to Yogev", wk: 1, day: 2 },
    { t: "Enterprise-Claude migration test", wk: 1 }, { t: "PO agent row-insert tool", wk: 2 } ] },
  { name: "Air Co", color: "#B07A1E", status: "~$30K deal · $50M co", acts: [
    { t: "Great intro meeting", wk: 0, day: 4, done: true }, { t: "Send proposal (~$30K)", wk: 0, day: 6 }, { t: "Follow-up / close", wk: 1 } ] },
  { name: "Arab Pulse", color: "#1E9E8F", status: "v1 live · Atlas", acts: [
    { t: "v1 shipped live", wk: 0, day: 5, done: true }, { t: "Escalation Watch added", wk: 0, day: 5, done: true },
    { t: "Erez review", wk: 0, day: 6 }, { t: "Phase-2 sources + auto-refresh", wk: 1 }, { t: "Product naming + lawyer OK", wk: 2 } ] },
  { name: "MyJarvis OS", color: "#4A534D", status: "Scaling us", acts: [
    { t: "Planning board shipped", wk: 0, day: 5, done: true }, { t: "Atlas on Telegram", wk: 0, day: 5, done: true },
    { t: "Telegram for all agents", wk: 0, day: 6 }, { t: "Revive Dave (YT knowledge)", wk: 1 }, { t: "Server migration (keystone)", wk: 3 }, { t: "Fast-agent tier", wk: 6 } ] },
];

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const TODAY = new Date();
function weekStart0() { const x = new Date(TODAY); x.setDate(x.getDate() - x.getDay()); x.setHours(0, 0, 0, 0); return x; }
const WS0 = weekStart0();
function addDays(d: Date, n: number) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
const fmtD = (d: Date) => `${MON[d.getMonth()]} ${d.getDate()}`;
const isToday = (d: Date) => d.toDateString() === TODAY.toDateString();
const isWknd = (d: Date) => d.getDay() === 5 || d.getDay() === 6;
const offOf = (a: Act) => a.wk * 7 + (a.day ?? 3);
const dateOf = (a: Act) => addDays(WS0, offOf(a));
type Item = { a: Act; p: Proj; date: Date };
const ITEMS: Item[] = P.flatMap((p) => p.acts.map((a) => ({ a, p, date: dateOf(a) })));
const itemsOn = (d: Date) => ITEMS.filter((it) => it.date.toDateString() === d.toDateString());

function dot(c: string, s = 9) { return <span style={{ width: s, height: s, borderRadius: 999, background: c, flexShrink: 0, display: "inline-block" }} />; }
const desc = (s: string) => <div style={{ fontSize: 14, color: T.ink2, marginBottom: 16, maxWidth: 1100 }}>{s}</div>;
function hexA(hex: string, a: number) { const n = parseInt(hex.slice(1), 16); return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`; }
function darken(hex: string, f: number) { const n = parseInt(hex.slice(1), 16); return `rgb(${Math.round(((n >> 16) & 255) * f)},${Math.round(((n >> 8) & 255) * f)},${Math.round((n & 255) * f)})`; }

// a goal pill (solid=planned, muted+✓=done)
function Pill({ a, color, size = 11 }: { a: Act; color: string; size?: number }) {
  const done = a.done;
  // Uniform light box for EVERY item (done + planned identical style) so week 1
  // and week 2 read the same — the ✓ prefix is the only done/planned signal.
  return <div style={{ background: hexA(color, 0.16), color: darken(color, 0.55), borderRadius: 7, padding: "4px 9px", fontSize: size, fontWeight: 600, lineHeight: 1.2, border: `1px solid ${hexA(color, 0.35)}` }}>{done ? "✓ " : ""}{a.t}</div>;
}

// ═══════════════ 3-MONTH · timeline (strips + project-named cards) ═══════════════
const QW = 13, TRACKW = 1620, LABELW = 190;
const weekX = (wk: number) => (wk / QW) * TRACKW;
const NOWX = weekX((TODAY.getTime() - WS0.getTime()) / (7 * 864e5));
function TimelineFinal() {
  const stripH = 80, pillW = 176;
  return (
    <div style={{ fontFamily: FONT }}>
      {desc("The quarter: each project a strip, its milestones as cards inside on the 3-month line. ✓ = already done this week. Each card leads with the project.")}
      <div style={{ background: T.card, border: `1px solid ${T.hair}`, borderRadius: 18, padding: 22, overflow: "hidden" }}>
        <div style={{ display: "flex", marginBottom: 12 }}>
          <div style={{ width: LABELW, flexShrink: 0 }} />
          <div style={{ position: "relative", width: TRACKW, height: 20 }}>
            {Array.from({ length: 4 }, (_, i) => { const d = addDays(WS0, i * 28); return <div key={i} style={{ position: "absolute", left: weekX(i * 4) + 6, ...EY, fontSize: 11.5 }}>{MON[d.getMonth()].toUpperCase()}</div>; })}
            <div style={{ position: "absolute", left: NOWX, ...EY, fontSize: 10, color: T.nowStroke, fontWeight: 700 }}>NOW</div>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {P.map((p) => {
            const rows: number[][] = [[], []];
            return (
              <div key={p.name} style={{ display: "flex", alignItems: "stretch" }}>
                <div style={{ width: LABELW, flexShrink: 0, paddingRight: 14, display: "flex", flexDirection: "column", justifyContent: "center", gap: 2 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>{dot(p.color, 10)}<span style={{ fontSize: 14.5, fontWeight: 700, color: T.ink }}>{p.name}</span></div>
                  <span style={{ ...EY, fontSize: 9.5, marginLeft: 18 }}>{p.status}</span>
                </div>
                <div style={{ position: "relative", width: TRACKW, height: stripH, borderRadius: 14, background: `linear-gradient(90deg, ${hexA(p.color, 0.13)}, ${hexA(p.color, 0.05)})`, border: `1px solid ${hexA(p.color, 0.3)}` }}>
                  {[4, 8, 12].map((w) => <div key={w} style={{ position: "absolute", top: 8, bottom: 8, left: weekX(w), width: 1, background: hexA(p.color, 0.2) }} />)}
                  <div style={{ position: "absolute", top: 0, bottom: 0, left: NOWX, width: 2, background: T.nowStroke, opacity: 0.5 }} />
                  <div style={{ position: "absolute", left: 10, right: 10, top: stripH / 2 - 1, height: 2, background: hexA(p.color, 0.25), borderRadius: 2 }} />
                  {[...p.acts].sort((a, b) => offOf(a) - offOf(b)).map((a, j) => {
                    let x = Math.min(Math.max(weekX(offOf(a) / 7), 6), TRACKW - pillW - 6);
                    let r = 0; if (rows[0].length && x < rows[0][rows[0].length - 1] + 6) r = 1; if (r === 1 && rows[1].length && x < rows[1][rows[1].length - 1] + 6) { r = 0; x = rows[0][rows[0].length - 1] + 8; }
                    rows[r].push(x + pillW); const top = r === 0 ? 6 : stripH - 40;
                    return (
                      <div key={j} style={{ position: "absolute", left: x, top: top + 6, width: pillW, boxSizing: "border-box", background: a.done ? hexA(p.color, 0.16) : p.color, color: a.done ? darken(p.color, 0.55) : "#fff", border: a.done ? `1px solid ${hexA(p.color, 0.35)}` : "none", borderRadius: 7, padding: "5px 9px", boxShadow: a.done ? "none" : "0 2px 6px rgba(0,0,0,0.15)", fontSize: 11.5, fontWeight: 600, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={`${a.t} · ${fmtD(dateOf(a))}`}>{a.done ? "✓ " : ""}{a.t}</div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ═══════════════ MONTH · filled pills ═══════════════
const monthWeeks = () => Array.from({ length: 4 }, (_, w) => Array.from({ length: 7 }, (_, d) => addDays(WS0, w * 7 + d)));
function MonthFinal() {
  return (
    <div style={{ fontFamily: FONT }}>
      {desc("The month: four week-rows of days; each goal a box in its project's colour. ✓ = done, no ✓ = planned.")}
      <div style={{ background: T.card, border: `1px solid ${T.hair}`, borderRadius: 14, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "70px repeat(7, 200px)" }}>
          <div style={{ background: T.bone, borderBottom: `1px solid ${T.hair}` }} />
          {DOW.map((d) => <div key={d} style={{ padding: "9px 12px", background: T.bone, ...EY, fontSize: 11, borderBottom: `1px solid ${T.hair}`, borderLeft: `1px solid ${T.line}` }}>{d}</div>)}
          {monthWeeks().map((wk, wi) => (
            <Fragment key={wi}>
              <div style={{ padding: "12px 8px", borderBottom: `1px solid ${T.line}`, ...EY, fontSize: 10, display: "flex", alignItems: "center" }}>W{wi + 1}</div>
              {wk.map((d, di) => (
                <div key={di} style={{ minHeight: 108, padding: 8, borderBottom: `1px solid ${T.line}`, borderLeft: `1px solid ${T.line}`, background: isToday(d) ? T.now : isWknd(d) ? "#FBFAF7" : "transparent", display: "flex", flexDirection: "column", gap: 5 }}>
                  <div style={{ fontFamily: MONO, fontSize: 11.5, color: isToday(d) ? T.nowStroke : T.faint }}>{fmtD(d)}</div>
                  {itemsOn(d).map((it, j) => <Pill key={j} a={it.a} color={it.p.color} />)}
                </div>
              ))}
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════ TWO WEEKS · project rows × 14 days ═══════════════
const twoWeekDays = () => Array.from({ length: 14 }, (_, i) => addDays(WS0, i));
function TwoWeekFinal() {
  const days = twoWeekDays(); const colW = 118;
  const [sel, setSel] = useState<string | null>(null);
  const SELBG = "#EAEAEA"; // light-gray row highlight
  return (
    <div style={{ fontFamily: FONT }}>
      {desc("This week + next, by day. ✓ = what already happened this week; no ✓ = the plan — tomorrow (Sat), next week, ahead. Weekends dimmed, today tinted.")}
      <div style={{ background: T.card, border: `1px solid ${T.hair}`, borderRadius: 14, overflow: "hidden", display: "grid", gridTemplateColumns: `150px repeat(14, ${colW}px)` }}>
        <div style={{ background: T.bone, borderBottom: `1px solid ${T.hair}` }} />
        {days.map((d, i) => (
          <div key={i} style={{ padding: "8px 6px", background: isToday(d) ? T.now : isWknd(d) ? "#FBFAF7" : T.bone, borderBottom: `1px solid ${T.hair}`, borderLeft: i === 7 ? `2px solid ${T.hair}` : `1px solid ${T.line}` }}>
            <div style={{ ...EY, fontSize: 9.5, color: isToday(d) ? T.nowStroke : T.faint }}>{DOW[d.getDay()]}{isToday(d) ? " · today" : ""}</div>
            <div style={{ fontFamily: MONO, fontSize: 11, color: T.faint }}>{d.getDate()}</div>
          </div>
        ))}
        {P.map((p) => {
          const on = sel === p.name;
          const toggle = () => setSel(on ? null : p.name);
          return (
          <Fragment key={p.name}>
            <div onClick={toggle} style={{ padding: "10px 12px", borderBottom: `1px solid ${T.line}`, borderLeft: `4px solid ${p.color}`, background: on ? SELBG : "transparent", cursor: "pointer", display: "flex", flexDirection: "column", gap: 1, justifyContent: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>{dot(p.color)}<span style={{ fontSize: 12.5, fontWeight: 700, color: T.ink }}>{p.name}</span></div>
              <span style={{ ...EY, fontSize: 8.5, marginLeft: 16 }}>{p.status}</span>
            </div>
            {days.map((d, di) => (
              <div key={di} onClick={toggle} style={{ minHeight: 54, padding: 5, borderBottom: `1px solid ${T.line}`, borderLeft: di === 7 ? `2px solid ${T.hair}` : `1px solid ${T.line}`, background: on ? SELBG : isToday(d) ? "#F6FBF8" : isWknd(d) ? "#FBFAF7" : "transparent", cursor: "pointer", display: "flex", flexDirection: "column", gap: 4 }}>
                {ITEMS.filter((it) => it.p === p && it.date.toDateString() === d.toDateString()).map((it, j) => <Pill key={j} a={it.a} color={p.color} size={10.5} />)}
              </div>
            ))}
          </Fragment>
          );
        })}
      </div>
    </div>
  );
}

export function PlanningVariations() {
  return (
    <>
      <SectionBox title="2-Week" layout="custom"><div style={{ marginTop: 0 }}><TwoWeekFinal /></div></SectionBox>
      <SectionBox title="Month" layout="custom"><div style={{ marginTop: 0 }}><MonthFinal /></div></SectionBox>
      <SectionBox title="3-Month" layout="custom"><div style={{ marginTop: 0 }}><TimelineFinal /></div></SectionBox>
    </>
  );
}
